import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ServiceBaseConfig } from '../../services/Interfaces/IAccountService';
import { IncorporationApiData, IncorporationApiResponse, IncorporationService } from '../../services/IncorporationService';
import { useApi } from '../../services/hooks/apiHook';
import { useAuth } from '../../store/auth';
import { readIncorporationError } from './apiError';
import { canTrackCompany } from './eligibility';
import { mapIncorporationTracking } from './mapIncorporation';
import { IncorporationCompanyRef, IncorporationTracking } from './types';

export interface IncorporationActionResult {
  ok: boolean;
  message: string;
}

export const useIncorporationTracking = (company?: IncorporationCompanyRef | null) => {
  const api = useApi();
  const { token } = useAuth();
  const { i18n } = useTranslation();
  const companyRef = useRef(company);
  companyRef.current = company;

  const companyId = company?.id ? String(company.id) : '';
  const companyStatus = company?.status ?? '';
  const companyType = company?.type ?? '';
  const eligible = canTrackCompany(company);

  const [tracking, setTracking] = useState<IncorporationTracking | null>(null);
  const [loading, setLoading] = useState(eligible);
  const [error, setError] = useState('');
  const trackingRef = useRef<IncorporationTracking | null>(null);
  const requestSeq = useRef(0);

  const label = useCallback((key: string) => String(i18n.t(key) || ''), [i18n]);

  const requestConfig = useCallback(
    (): ServiceBaseConfig => ({
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Language': (i18n.language || 'pt').toLowerCase()
      }
    }),
    [i18n.language, token]
  );

  const applyPayload = useCallback((id: string, data: IncorporationApiData) => {
    const next = mapIncorporationTracking(id, data);
    trackingRef.current = next;
    setTracking(next);
    setError('');
    return next;
  }, []);

  const refresh = useCallback(async () => {
    const current = companyRef.current;
    const seq = ++requestSeq.current;

    if (!current?.id || !canTrackCompany(current)) {
      trackingRef.current = null;
      setTracking(null);
      setError('');
      setLoading(false);
      return;
    }

    if (!token) {
      setLoading(true);
      return;
    }

    const sameCompany = trackingRef.current?.companyId === String(current.id);
    if (!sameCompany) {
      trackingRef.current = null;
      setTracking(null);
      setError('');
    }
    if (!trackingRef.current) setLoading(true);

    try {
      const response = await IncorporationService.getTracking(api, current.id, requestConfig());
      if (seq !== requestSeq.current) return;
      if (!response?.success || !response.data) {
        if (!sameCompany) {
          trackingRef.current = null;
          setTracking(null);
        }
        setError(response?.message || label('incorporation.errors.load'));
        return;
      }
      applyPayload(String(current.id), response.data);
    } catch (requestError) {
      if (seq !== requestSeq.current) return;
      if (!sameCompany) {
        trackingRef.current = null;
        setTracking(null);
      }
      setError(readIncorporationError(requestError, label('incorporation.errors.load')));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [api, applyPayload, companyId, companyStatus, companyType, i18n, label, requestConfig, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const mutate = useCallback(
    async (request: () => Promise<IncorporationApiResponse>): Promise<IncorporationActionResult> => {
      const current = companyRef.current;
      if (!current?.id) {
        return { ok: false, message: label('incorporation.errors.action') };
      }

      try {
        const response = await request();
        if (!response?.success || !response.data) {
          return { ok: false, message: response?.message || label('incorporation.errors.action') };
        }
        applyPayload(String(current.id), response.data);
        return { ok: true, message: response.message || '' };
      } catch (requestError) {
        return { ok: false, message: readIncorporationError(requestError, label('incorporation.errors.action')) };
      }
    },
    [applyPayload, label]
  );

  const uploadDocument = useCallback(
    (pendencyId: number, file: File) => {
      const current = companyRef.current;
      if (!current?.id) {
        return Promise.resolve({ ok: false, message: label('incorporation.errors.action') });
      }
      return mutate(() => IncorporationService.uploadDocument(api, current.id, pendencyId, file, requestConfig()));
    },
    [api, label, mutate, requestConfig]
  );

  const advanceStep = useCallback(() => {
    const current = companyRef.current;
    if (!current?.id) {
      return Promise.resolve({ ok: false, message: label('incorporation.errors.action') });
    }
    return mutate(() => IncorporationService.advance(api, current.id, requestConfig()));
  }, [api, label, mutate, requestConfig]);

  const createPendency = useCallback(
    (description: string) => {
      const current = companyRef.current;
      if (!current?.id) {
        return Promise.resolve({ ok: false, message: label('incorporation.errors.action') });
      }
      return mutate(() => IncorporationService.createPendency(api, current.id, description, requestConfig()));
    },
    [api, label, mutate, requestConfig]
  );

  return { tracking, loading, error, refresh, uploadDocument, advanceStep, createPendency };
};
